import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useDispatch,
  useFileCollection,
  useFileCollectionsFromParentId,
  useFilesFromFileCollectionId,
} from '@witmetrics/data-store';
import { COLLECTION_VIEWS, type CollectionView } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { useAppState } from '@/providers/AppStateProvider';
import { downloadFileCollection, fetchCollectionContents } from '@/utils/files';
import Card from '@/components/Card';
import Spinner from '@/components/Spinner';
import TruncatedText from '@/components/TruncatedText';
import DownloadToaster from '@/toasters/DownloadToaster';
import DeleteFileCollectionDialog from '@/dialogs/DeleteFileCollectionDialog';
import FileCollectionDialog from '@/dialogs/FileCollectionDialog';
import MoveContentDialog from '@/dialogs/MoveContentDialog';
import ContentMenu from '@/menus/ContentMenu';
import { FolderIcon, OptionsIcon } from '@/icons';
import FileCollection from '@/components/FileCollection';
import ListItem from '../FileCollectionList/ListItem';
import GridItem from '../FileCollectionGrid/GridItem';
import type { BaseItemProps, CollectionVariant } from '../types';
import { classes, getCountLabel, styles } from './utils';

export type FileCollectionItemProps = {
  active?: boolean;
  dense?: boolean;
  showDetails?: boolean;
  defaultExpanded?: boolean;
  variant?: CollectionVariant;
  className?: string;
  fileCollectionId: number;
  view?: CollectionView;
  onEdit?: () => void;
  onDelete?: () => void;
  onViewFile?: (fileId: number) => void;
  onViewFileCollection: (fileCollectionId: number) => void;
  onOpenFileCollection?: (fileCollectionId: number) => void;
};

export default function FileCollectionItem({
  active,
  dense,
  showDetails,
  defaultExpanded,
  variant,
  className,
  fileCollectionId,
  view = COLLECTION_VIEWS.LIST,
  onViewFile,
  onViewFileCollection,
  onOpenFileCollection,
  onEdit,
  onDelete,
}: FileCollectionItemProps) {
  const menuToggleRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const fileCollection = useFileCollection(fileCollectionId);
  const files = useFilesFromFileCollectionId(fileCollectionId);
  const childCollections = useFileCollectionsFromParentId(fileCollectionId);
  const [downloadMessage, setDownloadMessage] = useString(
    'Preparing collection...'
  );
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isMoveDialogOpen, toggleMoveDialog] = useToggle();
  const [isEditDialogOpen, toggleEditDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();
  const [isFetchingData, toggleFetchingData] = useToggle();
  const [isExpanded, toggleExpanded] = useToggle(defaultExpanded);

  const handleExpand = async () => {
    try {
      if (view === COLLECTION_VIEWS.GRID && onOpenFileCollection) {
        return onOpenFileCollection(fileCollectionId);
      }
      if (isExpanded) return toggleExpanded(false);
      toggleExpanded(true);
      toggleFetchingData(true);
      // TODO: Pagination
      const data = await fetchCollectionContents(fileCollectionId);
      dispatch.addFileCollections(data.fileCollections.results);
      dispatch.addFileCollectionFiles(data.files.results);
      toggleFetchingData(false);
    } catch (err) {
      toggleExpanded(false);
      toggleFetchingData(false);
      onApiError(err, 'Error fetching collection contents');
    }
  };

  const handleDownload = async () => {
    try {
      toggleMenu(false);
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      await downloadFileCollection(fileCollectionId, setDownloadMessage);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading collection');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  const renderIcon = () => {
    if (isAurora) {
      return (
        <span className={classes.auroraIcon} onClick={handleExpand}>
          {isFetchingData ? (
            <Spinner size={20} />
          ) : (
            <FolderIcon className={classes.auroraFolderIcon} />
          )}
        </span>
      );
    }
    return isFetchingData ? (
      <Spinner className={classes.spinner} size={20} onClick={handleExpand} />
    ) : (
      <FolderIcon className={classes.icon} onClick={handleExpand} />
    );
  };

  if (!fileCollection) return null;

  const isAurora = variant === 'aurora';

  const fileCount = getCountLabel(fileCollection);

  const dialogs = (
    <>
      {isDeleteDialogOpen && (
        <DeleteFileCollectionDialog
          fileCollectionId={fileCollectionId}
          onDelete={onDelete}
          onClose={toggleDeleteDialog}
        />
      )}
      {isEditDialogOpen && (
        <FileCollectionDialog
          fileCollectionId={fileCollectionId}
          onSave={onEdit}
          onClose={toggleEditDialog}
        />
      )}
      {isMoveDialogOpen && (
        <MoveContentDialog
          fileCollectionId={fileCollectionId}
          onClose={() => toggleMoveDialog(false)}
        />
      )}
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message={downloadMessage}
        onClose={handleCloseDownloadToaster}
      />
    </>
  );

  if (showDetails) {
    const sharedProps: BaseItemProps = {
      variant,
      overrideIcon: isAurora,
      name: fileCollection.name,
      icon: renderIcon(),
      onView: () => onViewFileCollection(fileCollectionId),
      onDownload: handleDownload,
      onMove: toggleMoveDialog,
      onEdit: toggleEditDialog,
      onDelete: toggleDeleteDialog,
    };

    return (
      <>
        {dialogs}
        {view === COLLECTION_VIEWS.LIST ? (
          <ListItem
            {...sharedProps}
            details={fileCount}
            updatedAt={fileCollection.updatedAt}
          />
        ) : (
          <GridItem compact {...sharedProps} tag={fileCount} />
        )}
        {isExpanded && onViewFile && (
          <FileCollection
            hideHeader
            className={classes.subCollection}
            variant={variant}
            listClassName={isAurora ? classes.subCollectionList : undefined}
            collectionView="LIST"
            fileCollections={childCollections}
            files={files}
            onViewFile={onViewFile}
            onViewFileCollection={onViewFileCollection}
          />
        )}
      </>
    );
  }

  return (
    <>
      {dialogs}
      <Card
        sx={styles.card}
        className={twMerge(
          isAurora ? classes.auroraCard : classes.card,
          dense && classes.cardDense,
          active && (isAurora ? classes.auroraActive : classes.active),
          className
        )}
        onClick={() => onViewFileCollection(fileCollectionId)}>
        <div
          className={twMerge(classes.content, dense && classes.contentDense)}>
          <FolderIcon
            className={twMerge(
              classes.folderIcon,
              isAurora && classes.auroraFolderTint
            )}
          />
          <TruncatedText
            className={classes.titleWrapper}
            textClassName={isAurora ? classes.auroraCardTitle : classes.title}>
            {fileCollection.name}
          </TruncatedText>
          <div
            className={classes.optionsWrapper}
            onClick={(e) => e.stopPropagation()}>
            <OptionsIcon
              ref={menuToggleRef}
              className={twMerge(
                classes.optionsIcon,
                isMenuOpen && classes.optionsIconOpen
              )}
              onClick={() => toggleMenu()}
            />
            <ContentMenu
              anchorTo={menuToggleRef.current}
              open={isMenuOpen}
              onDownload={handleDownload}
              onMove={toggleMoveDialog}
              onView={() => onViewFileCollection(fileCollectionId)}
              onEdit={toggleEditDialog}
              onDelete={toggleDeleteDialog}
              onClose={() => toggleMenu(false)}
            />
          </div>
        </div>
      </Card>
    </>
  );
}
